import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

/**
 * Reusable two-column full-width form layout.
 *
 * Left column: the form fields (primary input).
 * Right column: supporting content — gallery uploader, live preview,
 * context card, map, etc. Anything that benefits from being visible
 * alongside the fields instead of scrolled below them.
 *
 * On small screens the aside stacks below the form.
 *
 * The component is layout-only — it doesn't own the `<form>` element.
 * Consumers wrap `<form onSubmit={...}>` around `<FormLayout>` so the
 * submit button in `footer` still triggers the form, or render their
 * own `component="form"` on the outer Container.
 */

export interface FormLayoutProps {
  title: string;
  subtitle?: string;
  error?: string | null;
  /** Rendered above the title, inside the container — typically a PageHeader. */
  header?: ReactNode;
  /** The form fields. Rendered in the left column on md+. */
  form: ReactNode;
  /** Supporting content. Rendered in the right column on md+. */
  aside?: ReactNode;
  /** Action row (submit/cancel). Rendered full-width under both columns. */
  footer?: ReactNode;
  /** Width split for md+. Defaults to 7/5 so the form has more room. */
  columns?: { form: number; aside: number };
}

export function FormLayout({
  title,
  subtitle,
  error,
  header,
  form,
  aside,
  footer,
  columns = { form: 7, aside: 5 },
}: FormLayoutProps) {
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        {header}

        <Box>
          <Typography variant="h4">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid size={{ xs: 12, md: aside ? columns.form : 12 }}>
            <Stack spacing={3}>{form}</Stack>
          </Grid>
          {aside && (
            <Grid size={{ xs: 12, md: columns.aside }}>
              {aside}
            </Grid>
          )}
        </Grid>

        {footer && <Box sx={{ pt: 1 }}>{footer}</Box>}
      </Stack>
    </Container>
  );
}
